import {
  ApartmentOutlined,
  BankOutlined,
  BarChartOutlined,
  DollarOutlined,
  FileTextOutlined,
  IdcardOutlined,
  TeamOutlined,
  ToolOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { Menu } from 'antd';
import { NavLink } from 'react-router-dom';

import { ReactComponent as Line } from '../../assets/line.svg';
import { ReactComponent as Logo } from '../../assets/logo.svg';
import { ReactComponent as Logout } from '../../assets/logout.svg';
import { ReactComponent as Arrow } from '../../assets/menu-arrow.svg';
import { routes } from '../../routes';
import { logout } from '../../utils/logout';
import { usePermissions } from '../../hooks/usePermissions';

import styles from './dashboardLayout.module.scss';

type ChildItem = {
  key: string;
  label: React.ReactNode;
  icon: React.ReactNode;
  path: string;
};

type GroupItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  children: ChildItem[];
};

const baseItems: GroupItem[] = [
  {
    key: 'admission',
    label: 'Admission',
    icon: <UserAddOutlined />,
    children: [
      { key: 'users', label: <NavLink to={routes.userMgt.users}>Applicants</NavLink>, icon: <Line />, path: routes.userMgt.users },
      { key: 'requiredDocument', label: <NavLink to={routes.setup.requiredDocument}>Required Documents</NavLink>, icon: <Line />, path: routes.setup.requiredDocument },
      { key: 'schoolConfig', label: <NavLink to={routes.setup.schoolConfig}>School Feature Config</NavLink>, icon: <Line />, path: routes.setup.schoolConfig },
      { key: 'matricFormat', label: <NavLink to={routes.setup.matricFormat}>Matric Format</NavLink>, icon: <Line />, path: routes.setup.matricFormat },
      { key: 'admissionApproval', label: <NavLink to={routes.setup.admissionApproval}>Admission Approval Stages</NavLink>, icon: <Line />, path: routes.setup.admissionApproval },
      { key: 'admissionLetter', label: <NavLink to={routes.setup.admissionLetter}>Print Admission Letter</NavLink>, icon: <Line />, path: routes.setup.admissionLetter },
    ],
  },
  {
    key: 'academicStructure',
    label: 'Academic Structure',
    icon: <ApartmentOutlined />,
    children: [
      { key: 'school', label: <NavLink to={routes.setup.faculty}>School</NavLink>, icon: <Line />, path: routes.setup.faculty },
      { key: 'department', label: <NavLink to={routes.setup.department}>Department</NavLink>, icon: <Line />, path: routes.setup.department },
      { key: 'program', label: <NavLink to={routes.setup.program}>Program</NavLink>, icon: <Line />, path: routes.setup.program },
      { key: 'programType', label: <NavLink to={routes.setup.programType}>Program Type</NavLink>, icon: <Line />, path: routes.setup.programType },
      { key: 'specialization', label: <NavLink to={routes.setup.specialization}>Specialization</NavLink>, icon: <Line />, path: routes.setup.specialization },
      { key: 'sessionSetup', label: <NavLink to={routes.setup.session}>Session</NavLink>, icon: <Line />, path: routes.setup.session },
      { key: 'applicationBatch', label: <NavLink to={routes.setup.applicationBatch}>Application Batch</NavLink>, icon: <Line />, path: routes.setup.applicationBatch },
      { key: 'modeOfStudy', label: <NavLink to={routes.setup.modeOfStudy}>Mode of Study</NavLink>, icon: <Line />, path: routes.setup.modeOfStudy },
      { key: 'level', label: <NavLink to={routes.setup.level}>Level</NavLink>, icon: <Line />, path: routes.setup.level },
      { key: 'tuitionYears', label: <NavLink to={routes.setup.tuitionYears}>Tuition Years</NavLink>, icon: <Line />, path: routes.setup.tuitionYears },
    ],
  },
  {
    key: 'feesFinance',
    label: 'Fees SetUp',
    icon: <DollarOutlined />,
    children: [
      { key: 'applicationFee', label: <NavLink to={routes.setup.applicationFee}>Application Fee</NavLink>, icon: <Line />, path: routes.setup.applicationFee },
      { key: 'acceptanceFee', label: <NavLink to={routes.setup.acceptanceFee}>Acceptance Fee</NavLink>, icon: <Line />, path: routes.setup.acceptanceFee },
      { key: 'discountFee', label: <NavLink to={routes.setup.discountFee}>Discount</NavLink>, icon: <Line />, path: routes.setup.discountFee },
      { key: 'tuitionFee', label: <NavLink to={routes.setup.tuitionFee}>Tuition Fee</NavLink>, icon: <Line />, path: routes.setup.tuitionFee },
      { key: 'paymentRequery', label: <NavLink to={routes.setup.paymentRequery}>Payment Requery</NavLink>, icon: <Line />, path: routes.setup.paymentRequery },
    ],
  },
  {
    key: 'programmeContent',
    label: 'Programme Content',
    icon: <FileTextOutlined />,
    children: [
      { key: 'readMoreProgram', label: <NavLink to={routes.setup.readMoreProgram}>Read More – Program</NavLink>, icon: <Line />, path: routes.setup.readMoreProgram },
      { key: 'readMoreCourse', label: <NavLink to={routes.setup.readMoreCourse}>Read More – Course</NavLink>, icon: <Line />, path: routes.setup.readMoreCourse },
      { key: 'admissionRequirements', label: <NavLink to={routes.setup.readMoreAdmissionRequirement}>Admission Requirements</NavLink>, icon: <Line />, path: routes.setup.readMoreAdmissionRequirement },
      { key: 'careerProspects', label: <NavLink to={routes.setup.careerProspects}>Career Prospects</NavLink>, icon: <Line />, path: routes.setup.careerProspects },
      { key: 'curriculum', label: <NavLink to={routes.setup.curriculum}>Curriculum</NavLink>, icon: <Line />, path: routes.setup.curriculum },
      { key: 'accreditation', label: <NavLink to={routes.setup.accreditation}>Accreditation</NavLink>, icon: <Line />, path: routes.setup.accreditation },
      { key: 'testimonial', label: <NavLink to={routes.setup.testimony}>Testimonial</NavLink>, icon: <Line />, path: routes.setup.testimony },
    ],
  },
  {
    key: 'utilities',
    label: 'Utilities SetUp',
    icon: <ToolOutlined />,
    children: [
      { key: 'qualificationType', label: <NavLink to={routes.setup.qualification}>Qualification Type</NavLink>, icon: <Line />, path: routes.setup.qualification },
      { key: 'subject', label: <NavLink to={routes.setup.subject}>Subject</NavLink>, icon: <Line />, path: routes.setup.subject },
      { key: 'grade', label: <NavLink to={routes.setup.grade}>Grade</NavLink>, icon: <Line />, path: routes.setup.grade },
      { key: 'tab', label: <NavLink to={routes.setup.tab}>Tab</NavLink>, icon: <Line />, path: routes.setup.tab },
      { key: 'displayTab', label: <NavLink to={routes.setup.DisplayTabSetup}>Display Tab</NavLink>, icon: <Line />, path: routes.setup.DisplayTabSetup },
      { key: 'schoolId', label: <NavLink to={routes.userMgt.addSchoolId}>Lookup Setup</NavLink>, icon: <Line />, path: routes.userMgt.addSchoolId },
    ],
  },
  {
    key: 'biodata',
    label: 'Bio-Data Setup',
    icon: <IdcardOutlined />,
    children: [
      { key: 'addTitle', label: <NavLink to={routes.setup.title}>Title</NavLink>, icon: <Line />, path: routes.setup.title },
      { key: 'addGender', label: <NavLink to={routes.setup.gender}>Gender</NavLink>, icon: <Line />, path: routes.setup.gender },
      { key: 'addMaritalStatus', label: <NavLink to={routes.setup.maritalStatus}>Marital Status</NavLink>, icon: <Line />, path: routes.setup.maritalStatus },
      { key: 'addReligion', label: <NavLink to={routes.setup.religion}>Religion</NavLink>, icon: <Line />, path: routes.setup.religion },
      { key: 'addDisability', label: <NavLink to={routes.setup.disability}>Disability</NavLink>, icon: <Line />, path: routes.setup.disability },
      { key: 'addCountry', label: <NavLink to={routes.setup.country}>Country</NavLink>, icon: <Line />, path: routes.setup.country },
      { key: 'addState', label: <NavLink to={routes.setup.state}>State</NavLink>, icon: <Line />, path: routes.setup.state },
      { key: 'addLGA', label: <NavLink to={routes.setup.lga}>LGA</NavLink>, icon: <Line />, path: routes.setup.lga },
    ],
  },
  {
    key: 'schoolInfo',
    label: 'School Information',
    icon: <BankOutlined />,
    children: [
      { key: 'institution', label: <NavLink to={routes.setup.institution}>Institution</NavLink>, icon: <Line />, path: routes.setup.institution },
      { key: 'exploreProgrammes', label: <NavLink to={routes.setup.exploreProgrammes}>Explore Programmes</NavLink>, icon: <Line />, path: routes.setup.exploreProgrammes },
      { key: 'aboutUs', label: <NavLink to={routes.setup.aboutUs}>About Us</NavLink>, icon: <Line />, path: routes.setup.aboutUs },
      { key: 'whyUs', label: <NavLink to={routes.setup.whyUs}>Why Us</NavLink>, icon: <Line />, path: routes.setup.whyUs },
      { key: 'faq', label: <NavLink to={routes.setup.faq}>FAQ</NavLink>, icon: <Line />, path: routes.setup.faq },
      { key: 'newsAndEvents', label: <NavLink to={routes.setup.newsAndEvents}>News & Events</NavLink>, icon: <Line />, path: routes.setup.newsAndEvents },
      { key: 'history', label: <NavLink to={routes.setup.history}>History</NavLink>, icon: <Line />, path: routes.setup.history },
      { key: 'schoolMgt', label: <NavLink to={routes.setup.schoolMgt}>School Management</NavLink>, icon: <Line />, path: routes.setup.schoolMgt },
      { key: 'schoolInfoTemplate', label: <NavLink to={routes.setup.schoolInfo}>Template</NavLink>, icon: <Line />, path: routes.setup.schoolInfo },
      { key: 'socialMedia', label: <NavLink to={routes.setup.socialMedia}>Social Media</NavLink>, icon: <Line />, path: routes.setup.socialMedia },
      { key: 'studentLife', label: <NavLink to={routes.setup.studentLife}>Student Life</NavLink>, icon: <Line />, path: routes.setup.studentLife },
    ],
  },
  {
    key: 'userManagement',
    label: 'User Management',
    icon: <TeamOutlined />,
    children: [
      { key: 'admin-users', label: <NavLink to={routes.userMgt.adminUsers}>Admin Users</NavLink>, icon: <Line />, path: routes.userMgt.adminUsers },
      { key: 'role', label: <NavLink to={routes.userMgt.addRoles}>Admin Role</NavLink>, icon: <Line />, path: routes.userMgt.addRoles },
      { key: 'permissions', label: <NavLink to={routes.userMgt.permissions}>Permission Route</NavLink>, icon: <Line />, path: routes.userMgt.permissions },
      { key: 'privileges', label: <NavLink to={routes.userMgt.privileges}>Privileges</NavLink>, icon: <Line />, path: routes.userMgt.privileges },
      { key: 'emailTemplates', label: <NavLink to={routes.userMgt.emailTemplates}>Email Templates</NavLink>, icon: <Line />, path: routes.userMgt.emailTemplates },
      { key: 'contactUs', label: <NavLink to={routes.userMgt.contactUs}>Contact Feedback</NavLink>, icon: <Line />, path: routes.userMgt.contactUs },
      { key: 'resetAdminPassword', label: <NavLink to={routes.userMgt.resetAdminPassword}>Reset Users Password</NavLink>, icon: <Line />, path: routes.userMgt.resetAdminPassword },

    ],
  },
   {
    key: 'scholarshipManagement',
    label: 'Scholarship Management',
    icon: <TeamOutlined />,
    children: [
      { key: 'scholarship', label: <NavLink to={routes.setup.scholarship}>Scholarship</NavLink>, icon: <Line />, path: routes.setup.scholarship },
      { key: 'scholarshipConfig', label: <NavLink to={routes.setup.scholarshipConfig}>Scholarship Config</NavLink>, icon: <Line />, path: routes.setup.scholarshipConfig },
      { key: 'scholarshipApplication', label: <NavLink to={routes.setup.scholarshipApplication}>Scholarship Approvals</NavLink>, icon: <Line />, path: routes.setup.scholarshipApplication },
      { key: 'scholarshipApproval', label: <NavLink to={routes.setup.scholarshipApproval}>Scholarship App. Stages</NavLink>, icon: <Line />, path: routes.setup.scholarshipApproval },
      { key: 'scholarshipPercentageConfig', label: <NavLink to={routes.setup.scholarshipPercentageConfig}>Scholarship % Config</NavLink>, icon: <Line />, path: routes.setup.scholarshipPercentageConfig },
      // { key: 'privileges', label: <NavLink to={routes.userMgt.privileges}>Privileges</NavLink>, icon: <Line />, path: routes.userMgt.privileges },
      // { key: 'emailTemplates', label: <NavLink to={routes.userMgt.emailTemplates}>Email Templates</NavLink>, icon: <Line />, path: routes.userMgt.emailTemplates },
      // { key: 'contactUs', label: <NavLink to={routes.userMgt.contactUs}>Contact Feedback</NavLink>, icon: <Line />, path: routes.userMgt.contactUs },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    icon: <BarChartOutlined />,
    children: [
      { key: 'paymentReport', label: <NavLink to={routes.reports.paymentReport}>Payment Report</NavLink>, icon: <Line />, path: routes.reports.paymentReport },
      { key: 'applicantReport', label: <NavLink to={routes.reports.applicantReport}>Applicant Report</NavLink>, icon: <Line />, path: routes.reports.applicantReport },
      { key: 'scholarshipReport', label: <NavLink to={routes.reports.scholarshipReport}>Scholarship Report</NavLink>, icon: <Line />, path: routes.reports.scholarshipReport },
      { key: 'programSummary', label: <NavLink to={routes.reports.programSummary}>Applicants by Program</NavLink>, icon: <Line />, path: routes.reports.programSummary },

    ],
  },
];

const Sidebar = () => {
  const { canAccess, isLoading } = usePermissions();

  const items = baseItems
    .map((group) => ({
      ...group,
      children: group.children.filter((child) => canAccess(child.path)),
    }))
    .filter((group) => group.children.length > 0);

  return (
    <>
      <section>
        <div className={styles.wrapper}>
          <Logo className={styles.sidebarLogo} />
        </div>

        <div className={styles.menuItems}>
          <Menu
            items={isLoading ? [] : items}
            mode="inline"
            inlineIndent={0}
            defaultOpenKeys={['admission']}
            expandIcon={(info) => <Arrow className={info.isOpen ? 'rotate' : ''} />}
          />
        </div>
      </section>

      <button className={styles.logout} onClick={logout}>
        <Logout />
        <p className={styles.end}>Logout</p>
      </button>
    </>
  );
};

export default Sidebar;
